'use client';
import React, { useState } from 'react';
import { MapPin, Clock, Navigation } from 'lucide-react';

interface Destination {
  id: string;
  name: string;
  image: string;
  imageAlt: string;
  duration: string;
  distance: string;
  description: string;
  category: string;
}

const destinations: Destination[] = [
{
  id: 'dest-agra',
  name: 'Agra',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_118e7e561-1772252912875.png",
  imageAlt: 'Taj Mahal at sunrise with reflecting pool and lush gardens in Agra',
  duration: '1 Day',
  distance: '230 km',
  description: 'Visit the iconic Taj Mahal and Agra Fort with a comfortable cab.',
  category: 'Heritage'
},
{
  id: 'dest-mathura',
  name: 'Mathura & Vrindavan',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e8c39c7e-1772822027198.png",
  imageAlt: 'Colorful Krishna temple with devotees gathered during festival in Mathura',
  duration: '1 Day',
  distance: '150 km',
  description: 'Spiritual journey to the birthplace of Lord Krishna.',
  category: 'Religious'
},
{
  id: 'dest-jaipur',
  name: 'Jaipur',
  image: "https://images.unsplash.com/photo-1612105301362-0d4900e364fa",
  imageAlt: 'Hawa Mahal pink palace facade with ornate windows in Jaipur Rajasthan',
  duration: '2 Days',
  distance: '280 km',
  description: 'Explore the Pink City — Amber Fort, Hawa Mahal, City Palace.',
  category: 'Heritage'
},
{
  id: 'dest-kedarnath',
  name: 'Kedarnath',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_16ce56ba7-1772361620902.png",
  imageAlt: 'Kedarnath temple in snow covered Himalayan mountains with devotees',
  duration: '4 Days',
  distance: '450 km',
  description: 'Sacred Shiva temple in the majestic Himalayas.',
  category: 'Char Dham'
},
{
  id: 'dest-badrinath',
  name: 'Badrinath',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_16cb23ee6-1768229300908.png",
  imageAlt: 'Badrinath temple colorful facade surrounded by snow capped Himalayan peaks',
  duration: '4 Days',
  distance: '490 km',
  description: 'Lord Vishnu abode nestled in the beautiful Chamoli district.',
  category: 'Char Dham'
},
{
  id: 'dest-gangotri',
  name: 'Gangotri',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1130c55a6-1778585438847.png",
  imageAlt: 'Gangotri temple on river banks with Himalayan peaks and holy river Ganga',
  duration: '3 Days',
  distance: '470 km',
  description: 'Source of the holy river Ganga in Uttarkashi district.',
  category: 'Char Dham'
},
{
  id: 'dest-yamunotri',
  name: 'Yamunotri',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17bb85fbe-1778497055662.png",
  imageAlt: 'Yamunotri temple in mountain valley with hot springs and dense forest',
  duration: '3 Days',
  distance: '400 km',
  description: 'Origin of river Yamuna — a divine and scenic Himalayan trek.',
  category: 'Char Dham'
},
{
  id: 'dest-char-dham',
  name: 'Char Dham Yatra',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18dfaa241-1778592619779.png",
  imageAlt: 'Collage of all four Char Dham temples surrounded by Himalayan mountains',
  duration: '12 Days',
  distance: '1800 km',
  description: 'Complete Char Dham circuit — Kedarnath, Badrinath, Gangotri, Yamunotri.',
  category: 'Char Dham'
},
{
  id: 'dest-kashmir',
  name: 'Kashmir',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b7fceba7-1772248815810.png",
  imageAlt: 'Dal Lake Kashmir with shikara boats and snow capped mountains at sunset',
  duration: '7 Days',
  distance: '800 km',
  description: 'Heaven on Earth — Dal Lake, Gulmarg, Pahalgam, Sonamarg.',
  category: 'Hill Station'
},
{
  id: 'dest-rajasthan',
  name: 'Rajasthan Tour',
  image: "https://images.unsplash.com/photo-1709468864407-5fe48bb204ee",
  imageAlt: 'Mehrangarh Fort Jodhpur with blue city houses and desert landscape',
  duration: '5 Days',
  distance: '600 km',
  description: 'Jaipur, Jodhpur, Udaipur — royal Rajasthan in one grand tour.',
  category: 'Heritage'
},
{
  id: 'dest-punjab',
  name: 'Punjab & Amritsar',
  image: "https://images.unsplash.com/photo-1597114981569-379a03be6f5a",
  imageAlt: 'Golden Temple Amritsar illuminated at night with sacred sarovar reflecting golden light',
  duration: '2 Days',
  distance: '450 km',
  description: 'Golden Temple, Wagah Border, Jallianwala Bagh — pride of Punjab.',
  category: 'Religious'
}];


const categories = ['All', 'Char Dham', 'Heritage', 'Religious', 'Hill Station'];

export default function DestinationsSection() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All' ?
  destinations :
  destinations.filter((d) => d.category === activeCategory);

  const getWhatsAppMessage = (dest: Destination) =>
  `Hi Navya Travels! I want to book a trip to ${dest.name} (${dest.duration}). Please share the price and availability.`;

  return (
    <section id="destinations" className="py-20 px-6 lg:px-10 bg-black section-bg">
      <div className="max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="text-xs font-bold text-primary uppercase tracking-widest mb-3 block">
            Outstation Tours
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
            Popular <span className="gradient-blue-text">Destinations</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Explore India with trusted drivers. Outstation bookings via WhatsApp for personalised service.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {categories.map((cat) =>
          <button
            key={`cat-${cat}`}
            onClick={() => setActiveCategory(cat)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
            activeCategory === cat ?
            'bg-primary text-white glow-blue-sm' : 'btn-ghost'}`
            }>
            
              {cat}
            </button>
          )}
        </div>

        {/* Destinations Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((dest) =>
          <div
            key={dest.id}
            className="destination-card glass-card border border-border/20 card-hover">
            
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                src={dest.image}
                alt={dest.imageAlt}
                className="w-full h-full object-cover transition-transform duration-500" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent destination-overlay" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

                {/* Category Badge */}
                <div className="absolute top-3 left-3">
                  <span className="text-xs font-bold bg-primary/80 backdrop-blur-sm text-white px-2 py-1 rounded-full">
                    {dest.category}
                  </span>
                </div>

                {/* Price on enquiry badge */}
                <div className="absolute top-3 right-3">
                  <span className="text-xs font-semibold text-amber-300 bg-black/70 backdrop-blur-sm px-2 py-1 rounded-lg leading-tight text-center block">
                    Enquire for price
                  </span>
                </div>

                {/* Name overlay */}
                <div className="absolute bottom-3 left-3 right-3">
                  <h3 className="font-bold text-white text-lg leading-tight">{dest.name}</h3>
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-center gap-4 mb-3">
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock size={12} className="text-primary" />
                    {dest.duration}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Navigation size={12} className="text-primary" />
                    {dest.distance}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin size={12} className="text-primary" />
                    From Delhi
                  </span>
                </div>

                <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{dest.description}</p>

                {/* Price info note */}
                <div className="mb-3 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-xs text-amber-300 font-medium">
                    💬 Price will be shared by driver after enquiry.
                  </p>
                </div>

                {/* WhatsApp Book Button */}
                <a
                href={`https://wa.me/918860044910?text=${encodeURIComponent(getWhatsAppMessage(dest))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold bg-white text-black hover:bg-slate-100 transition-colors">
                
                  <svg className="w-4 h-4 fill-green-600" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  Book via WhatsApp
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">Don&apos;t see your destination? We cover all of India.</p>
          <a
            href="https://wa.me/918860044910?text=Hi%20Navya%20Travels!%20I%20need%20a%20custom%20outstation%20package.%20Please%20help."
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary px-8 py-3 rounded-xl font-bold inline-flex items-center gap-2">
            
            <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Request Custom Package
          </a>
        </div>
      </div>
    </section>);

}